import { Component, inject, computed, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Observable, take, tap } from 'rxjs';
import { Organization } from 'src/app/features/organization/organization-models';
import { AppState } from 'src/app/app.state';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { PricePolicyType, PricePolicy } from './interfaces/price-policy';
import { MatDialog } from '@angular/material/dialog';
import { PricePoliciesCreateOrEditDialogComponent } from './components/price-policies-create-or-edit-dialog/price-policies-create-or-edit-dialog.component';
import { ActionLogModalComponent } from 'src/app/shared/components/modals/actionlog-modal/action-log-modal/action-log-modal.component';
import { EntityType } from '../action-log';
import { PricePolicySearchType } from 'src/app/shared/enums/price-policy-search-type.enum';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { RequiresFeatureRoleDirective } from '../../../shared/directives/requires-feature-role.directive';
import { PricePolicyListStateService } from './services/price-policies.service';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';
import { CardComponent, ExpandListComponent, ExpandListConfiguration, ScreenSizeEnum } from 'ok-angular-shared/components';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { AuthService } from 'src/app/core/auth/auth.service';
import { NoteCreationModalComponent } from 'src/app/shared/components/modals/price-policy-note-creation-modal/note-creation-modal.component';
import { PricePolicyV2Service } from 'src/app/shared/services/price-policy-v2.service';
import { MatTooltip } from '@angular/material/tooltip';
import { SnackbarService } from 'ok-angular-shared/components';
import { getPricePolicyErrorMessage } from './helpers/price-policy-error.helper';
import { DkCurrencyPipe } from '../../../shared/pipes/dk-currency';
import { TranslatePricePolicyTypePipe } from '../../../shared/pipes/translate-price-policy-type';

@Component({
  selector: 'adm-price-policies',
  templateUrl: './price-policies.component.html',
  styleUrls: ['./price-policies.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    RequiresFeatureRoleDirective,
    AdmOrgTypeaheadComponent,
    TextSearchComponent,
    MatIcon,
    MatSlideToggle,
    MatButton,
    CardComponent,
    ExpandListComponent,
    MatPaginator,
    MatTooltip,
    DkCurrencyPipe,
    RouterLink,
    AdminEnumSelectListComponent,
    TranslatePricePolicyTypePipe,
  ],
  providers: [PricePolicyListStateService],
})
export class PricePoliciesComponent {
  // Inject services
  public appState = inject(AppState);
  public environmentLoaderService = inject(EnvironmentLoaderService);
  public matDialog = inject(MatDialog);
  public authService = inject(AuthService);
  public pricePolicyV2Service = inject(PricePolicyV2Service);
  public state = inject(PricePolicyListStateService);
  private snackbarService = inject(SnackbarService);

  public roles = this.environmentLoaderService.virtualRoles;
  public PricePolicySearchType = PricePolicySearchType;
  public PricePolicyType = PricePolicyType;
  public typeOptions = Object.values(PricePolicyType);
  public translatePricePolicyTypePipe = new TranslatePricePolicyTypePipe();

  // Header template references
  public headerBasePriceTemplate = viewChild<TemplateRef<unknown>>('headerBasePriceTemplate');
  public headerOkSupplementTemplate = viewChild<TemplateRef<unknown>>('headerOkSupplementTemplate');
  public headerCustomerSupplementTemplate = viewChild<TemplateRef<unknown>>('headerCustomerSupplementTemplate');
  public headerConsumerPriceTemplate = viewChild<TemplateRef<unknown>>('headerConsumerPriceTemplate');
  public headerConnectorTemplate = viewChild<TemplateRef<unknown>>('headerConnectorTemplate');

  // Row template references
  public rowTypeTemplate = viewChild<TemplateRef<unknown>>('rowTypeTemplate');
  public rowConnectingTemplate = viewChild<TemplateRef<unknown>>('rowConnectingTemplate');
  public rowBasePriceTemplate = viewChild<TemplateRef<unknown>>('rowBasePriceTemplate');
  public rowOkSupplementTemplate = viewChild<TemplateRef<unknown>>('rowOkSupplementTemplate');
  public rowCustomerSupplementTemplate = viewChild<TemplateRef<unknown>>('rowCustomerSupplementTemplate');
  public rowConsumerPriceTemplate = viewChild<TemplateRef<unknown>>('rowConsumerPriceTemplate');
  public rowConnectorsTemplate = viewChild<TemplateRef<unknown>>('rowConnectorsTemplate');

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.filters.update(x => ({
        ...x,
        query: value && value.length > 0 ? value : null,
        page: { ...x.page, pageIndex: 0 },
      }));
    });
  };

  organizationsFetched(orgnizations: Organization[] | null) {
    this.state.fetchedOrganizations.set(orgnizations);
  }

  organizationSelected = (org: Organization | null) => {
    this.state.filters.update(x => ({
      ...x,
      organizationId: org ? org.id : null,
      policySearchType: org ? PricePolicySearchType.All : x.policySearchType,
      page: { ...x.page, pageIndex: 0 },
    }));
  };

  toggleGlobalPrices(checked: boolean) {
    if (checked) {
      this.state.filters.update(x => ({
        ...x,
        policySearchType: PricePolicySearchType.GlobalOnly,
        organizationId: null,
        page: { ...x.page, pageIndex: 0 },
      }));
      this.state.resetAllFiltersTrigger.set({ reset: true });
    } else {
      this.state.filters.update(x => ({
        ...x,
        policySearchType: PricePolicySearchType.All,
        page: { ...x.page, pageIndex: 0 },
      }));
    }
  }

  typeSelected(type: string | null) {
    this.state.filters.update(x => ({
      ...x,
      type: type,
      page: { ...x.page, pageIndex: 0 },
    }));
  }

  public openActionLog() {
    this.matDialog.open(ActionLogModalComponent, {
      data: { organizations: this.state.fetchedOrganizations(), entityType: EntityType.PricePolicy },
      disableClose: true,
      width: '1024px',
    });
  }

  public openPricePolicyManagement(pricePolicy: PricePolicy | null) {
    const dialogRef = this.matDialog.open(PricePoliciesCreateOrEditDialogComponent, {
      data: { pricePolicy: pricePolicy },
      disableClose: true,
      width: '640px',
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        tap(result => {
          if (result && typeof result === 'string') {
            this.state.triggerUpdateWithHighlight(result);
          } else {
            this.state.triggerUpdate();
          }
        })
      )
      .subscribe();
  }

  public pageSwitch(event: PageEvent) {
    this.state.filters.update(f => ({ ...f, page: event }));
  }

  // Config for expand list
  public config = computed<ExpandListConfiguration<PricePolicy>>(() => ({
    expandDisabled: true,
    columns: [
      { field: 'name', header: 'Navn', width: '2fr', sortField: 'Name' },
      { field: 'type', header: 'Type', width: '180px', sortField: 'Type', displayTemplate: this.rowTypeTemplate()! },
      {
        field: 'organizationName',
        header: 'Tilknytning',
        width: '2fr',
        sortField: 'OrganizationName',
        displayTemplate: this.rowConnectingTemplate()!,
      },
      {
        field: 'basePriceInKrWithoutVat',
        header: this.headerBasePriceTemplate()!,
        width: '200px',
        sortField: 'BasePrice',
        displayTemplate: this.rowBasePriceTemplate()!,
      },
      {
        field: 'okSupplementInKrWithoutVat',
        header: this.headerOkSupplementTemplate()!,
        width: '120px',
        sortField: 'OkSupplement',
        displayTemplate: this.rowOkSupplementTemplate()!,
      },
      {
        field: 'organizationSupplementInKrWithoutVat',
        header: this.headerCustomerSupplementTemplate()!,
        width: '120px',
        sortField: 'OrganizationSupplement',
        displayTemplate: this.rowCustomerSupplementTemplate()!,
      },
      {
        field: 'consumerPriceInKrWithVat',
        header: this.headerConsumerPriceTemplate()!,
        width: '1fr',
        sortField: 'ConsumerPrice',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.rowConsumerPriceTemplate()!,
      },
      {
        field: 'connectorInfo',
        header: this.headerConnectorTemplate()!,
        width: '1fr',
        sortField: 'ConnectorCount',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.rowConnectorsTemplate()!,
      },
    ],
    classFn: (item: PricePolicy) => {
      if (item.highlight?.getValue()) {
        return 'highlight';
      }
      return '';
    },
    actions: [
      {
        icon: { name: 'edit-s' },
        label: 'Rediger prispolitik',
        action: (item?: PricePolicy) => item && this.editPricePolicy(item),
        hiddenFn: () => !this.authService.hasRole(this.roles[Permission.PricePolicyWrite]),
      },
      {
        icon: { name: 'trash-s' },
        label: 'Slet prispolitik',
        action: (item?: PricePolicy) => item && this.deletePricePolicy(item),
        hiddenFn: () => !this.authService.hasRole(this.roles[Permission.PricePolicyWrite]),
      },
    ],
  }));

  private editPricePolicy(pricePolicy: PricePolicy) {
    const dialogRef = this.matDialog.open(PricePoliciesCreateOrEditDialogComponent, {
      data: { pricePolicy: pricePolicy },
      disableClose: true,
      width: '640px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.pricePolicyV2Service.getPricePolicy(result).subscribe(updatedPolicy => {
          this.state.refreshPricePolicy(updatedPolicy);
        });
      }
    });
  }

  private deletePricePolicy(pricePolicy: PricePolicy) {
    const noteDialogRef = this.matDialog.open(NoteCreationModalComponent, {
      data: {
        title: 'Tilføj note omkring sletning',
        confirmText: 'Gem',
        denyText: 'Annuller',
        icon: { name: 'trash-s' },
      },
    });

    noteDialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.pricePolicyV2Service.deletePricePolicy(pricePolicy.id, data).subscribe({
          next: () => {
            this.state.triggerUpdate();
          },
          error: error => {
            const errorMessage = getPricePolicyErrorMessage(pricePolicy.name, error, 'delete');
            this.snackbarService.error(errorMessage);
          },
        });
      }
    });
  }
}
